import { useAtomValue, useSetAtom } from 'jotai'
import { useEffect, useRef, useState } from 'react'
import { ArrowLeft, BookOpenText, ChevronRight, Home, Plus } from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router'
import { toast } from 'sonner'
import { classroomsAtom, currentClassIdAtom, currentSessionIdAtom, isDarkAtom, sessionsAtom } from '../state/store'
import { useSessions } from '../hooks/useSessions'
import { getClassInitial, getClassroomPalette } from '../lib/classroom-theme'
import { useWorkspace } from '../contexts/WorkspaceContext'
import { SessionListItem } from './SessionListItem'
import { Button } from './ui/button'

export const CURRENT_CLASS_MENU_TITLE_CLASSNAME = 'truncate font-heading text-sm'

function getForegroundForPalette(color: string) {
  const match = color.match(/hsl\(\s*\d+\s+\d+%\s+(\d+)%\s*\)/i)
  const lightness = match ? Number(match[1]) : 50
  return lightness >= 68 ? 'text-[#202114]' : 'text-white'
}

export function SessionList() {
  const navigate = useNavigate()
  const location = useLocation()
  const prevPathRef = useRef(location.pathname)
  const [showClassDirectory, setShowClassDirectory] = useState(location.pathname === '/')
  const classrooms = useAtomValue(classroomsAtom)
  const currentClassId = useAtomValue(currentClassIdAtom)
  const sessions = useAtomValue(sessionsAtom)
  const currentSessionId = useAtomValue(currentSessionIdAtom)
  const isDark = useAtomValue(isDarkAtom)
  const { memberRole } = useWorkspace()
  const { createSession, selectSession, deleteSession } = useSessions()

  const currentClass = classrooms.find((item) => item.id === currentClassId) ?? null
  const generalChatItems = [...sessions]
    .filter((session) => !session.classId)
    .sort((a, b) => b.lastUsedAt - a.lastUsedAt)
    .slice(0, 8)
  const classChatItems = [...sessions]
    .filter((session) => session.classId === currentClassId)
    .sort((a, b) => b.lastUsedAt - a.lastUsedAt)
    .slice(0, 50)
  const currentClassInitial = currentClass?.title.trim().charAt(0).toUpperCase() || 'C'
  const currentClassPalette = currentClass ? getClassroomPalette(currentClass) : null
  const currentClassCardBg = currentClassPalette ? (isDark ? currentClassPalette.dark : currentClassPalette.light) : undefined
  const currentClassIconClass = isDark
    ? 'border-black/70 bg-black/20 text-white/80'
    : 'border-black/20 bg-white/55 text-foreground/80'
  const currentClassTextClass = isDark ? 'text-white/72' : 'text-foreground/80'
  const isMyClassesActive = location.pathname === '/'
  const isGeneralChatActive = location.pathname.startsWith('/c/')
  const isClassHomeActive = Boolean(currentClass && location.pathname === `/classes/${currentClass.id}`)
  const activeClasses = classrooms.filter((item) => item.status === 'IN_PROGRESS')
  const pastClasses = classrooms.filter((item) => item.status === 'ENDED')
  const disableNewClassChat = Boolean(currentClassId && memberRole === 'ADMIN')
  const activeView = currentClass
    ? 'class-detail'
    : isMyClassesActive && showClassDirectory
      ? 'class-directory'
      : 'selector'
  const activeViewIndex = activeView === 'selector' ? 0 : activeView === 'class-directory' ? 1 : 2

  useEffect(() => {
    const prevPath = prevPathRef.current
    if (location.pathname === '/') {
      if (prevPath !== '/') {
        setShowClassDirectory(true)
      }
    } else {
      setShowClassDirectory(false)
    }
    prevPathRef.current = location.pathname
  }, [location.pathname])

  const openClassroom = async (classroom: (typeof classrooms)[number]) => {
    if (memberRole === 'TEACHER' || memberRole === 'ADMIN' || classroom.isJoined) {
      navigate(`/classes/${classroom.id}`)
      return
    }
    toast.error('Use Join New Class from the My Classes page to enter a class code.')
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="relative flex-1 overflow-hidden">
        <div
          className="absolute inset-0 transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${activeViewIndex * 100}%)` }}
        >
          <div className="flex h-full w-[300%]">
            <div className="h-full w-1/3 shrink-0 overflow-y-auto">
              <div className="px-2 pt-2 pb-2">
                <div className="space-y-2">
                  <Link
                    to="/"
                    onClick={() => setShowClassDirectory(true)}
                    className={`flex min-h-12 w-full items-center justify-between gap-2 rounded-base border-2 px-3 py-3 text-sm font-heading transition-all duration-200 ${
                      isMyClassesActive && showClassDirectory
                        ? 'border-nb-border bg-main text-main-foreground nb-shadow'
                        : 'border-nb-border bg-secondary-bg text-foreground nb-shadow-sm hover:bg-main hover:text-main-foreground'
                    }`}
                  >
                    <span className="inline-flex items-center gap-2">
                      <BookOpenText className="h-4 w-4" />
                      <span>My Classes</span>
                    </span>
                    <ChevronRight className="h-4 w-4 opacity-60" />
                  </Link>
                  <button
                    className={`w-full flex items-center gap-2 rounded-base border-2 border-nb-border px-2.5 py-2 text-sm font-heading transition-all duration-200 ${
                      disableNewClassChat
                        ? 'cursor-not-allowed bg-foreground/10 text-foreground/30'
                        : isGeneralChatActive
                        ? 'bg-main text-main-foreground nb-shadow'
                        : 'bg-secondary-bg text-foreground nb-shadow-sm hover:bg-main hover:text-main-foreground'
                    } nb-hover-press`}
                    onClick={() => {
                      if (disableNewClassChat) return
                      void createSession()
                    }}
                    disabled={disableNewClassChat}
                  >
                    <Plus className="h-4 w-4" />
                    <span>New Conversation</span>
                  </button>
                </div>
              </div>
              <div className="pl-2 pr-3 pb-2">
                <div className="mb-2">
                  <p className="px-2.5 py-1 text-[11px] font-heading text-foreground/50 uppercase tracking-wider">
                    Recent Chats
                  </p>
                </div>
                {generalChatItems.length === 0 ? (
                  <p className="px-2.5 py-4 text-xs font-base text-foreground/40">
                    No recent chats yet.
                  </p>
                ) : (
                  generalChatItems.map((session) => (
                    <SessionListItem
                      key={session.id}
                      session={session}
                      isActive={session.id === currentSessionId}
                      showClassTitle
                      onSelect={() => selectSession(session.id)}
                      onDelete={() => deleteSession(session.id)}
                    />
                  ))
                )}
              </div>
            </div>

            <div className="h-full w-1/3 shrink-0 overflow-y-auto">
              <div className="pl-2 pr-3 pb-2">
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => setShowClassDirectory(false)}
                    className="mb-3 inline-flex items-center gap-2 rounded-base border-2 border-transparent px-2 py-1 text-sm text-foreground/60 transition-colors hover:border-nb-border hover:bg-secondary-bg hover:text-foreground"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    <span className="font-heading text-foreground">Back</span>
                  </button>

                  {activeClasses.length > 0 ? (
                    <div className="mb-4">
                      <p className="px-2.5 py-1 text-[11px] font-heading uppercase tracking-wider text-foreground/45">
                        Now
                      </p>
                      <div className="mt-1 space-y-1">
                        {activeClasses.map((classroom) => {
                          const palette = getClassroomPalette(classroom)
                          const classroomBg = isDark ? palette.dark : palette.light
                          const classroomIconClass = getForegroundForPalette(classroomBg)
                          const classroomMetaClass = isDark ? 'text-white/55' : 'text-foreground/55'

                          return (
                            <button
                              key={classroom.id}
                              type="button"
                              onClick={() => void openClassroom(classroom)}
                              className="flex w-full items-start gap-3 rounded-base border-2 border-transparent px-2.5 py-2 text-left transition-all duration-200 hover:border-nb-border hover:bg-secondary-bg"
                            >
                              <div
                                className={`flex size-9 shrink-0 items-center justify-center rounded-base border-2 border-black/20 text-sm font-heading ${classroomIconClass}`}
                                style={{ backgroundColor: classroomBg }}
                              >
                                {getClassInitial(classroom.title)}
                              </div>
                              <div className="min-w-0">
                                <p className="truncate font-heading text-sm text-foreground">{classroom.title}</p>
                                <p className={`truncate text-xs ${classroomMetaClass}`}>
                                  {classroom.description || classroom.topics.join(', ') || 'No topics yet'}
                                </p>
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ) : null}

                  {pastClasses.length > 0 ? (
                    <div>
                      <p className="px-2.5 py-1 text-[11px] font-heading uppercase tracking-wider text-foreground/45">
                        Ended
                      </p>
                      <div className="mt-1 space-y-1">
                        {pastClasses.map((classroom) => {
                          const palette = getClassroomPalette(classroom)
                          const classroomBg = isDark ? palette.dark : palette.light
                          const classroomIconClass = getForegroundForPalette(classroomBg)
                          const classroomMetaClass = isDark ? 'text-white/55' : 'text-foreground/55'

                          return (
                            <button
                              key={classroom.id}
                              type="button"
                              onClick={() => void openClassroom(classroom)}
                              className="flex w-full items-start gap-3 rounded-base border-2 border-transparent px-2.5 py-2 text-left transition-all duration-200 hover:border-nb-border hover:bg-secondary-bg"
                            >
                              <div
                                className={`flex size-9 shrink-0 items-center justify-center rounded-base border-2 border-black/20 text-sm font-heading ${classroomIconClass}`}
                                style={{ backgroundColor: classroomBg }}
                              >
                                {getClassInitial(classroom.title)}
                              </div>
                              <div className="min-w-0">
                                <p className="truncate font-heading text-sm text-foreground">{classroom.title}</p>
                                <p className={`truncate text-xs ${classroomMetaClass}`}>
                                  {classroom.description || classroom.topics.join(', ') || 'No topics yet'}
                                </p>
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="h-full w-1/3 shrink-0 overflow-y-auto">
              <div className="pl-2 pr-3 pb-2">
                {currentClass ? (
                  <div className="mb-4 pt-2">
                    <Link
                      to="/"
                      className="mb-2 inline-flex items-center gap-2 rounded-base border-2 border-transparent px-2 py-1 text-sm text-foreground/60 transition-colors hover:border-nb-border hover:bg-secondary-bg hover:text-foreground"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Back
                    </Link>
                    <div className="rounded-base border-2 border-nb-border px-3 py-3 nb-shadow-sm" style={{ backgroundColor: currentClassCardBg }}>
                      <div className="flex items-start gap-3">
                        <div className={`flex size-9 shrink-0 items-center justify-center rounded-base border-2 text-sm font-heading ${currentClassIconClass}`}>
                          {currentClass.icon ? (
                            <span className="text-lg leading-none">{currentClass.icon}</span>
                          ) : (
                            currentClassInitial
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p
                              className={`${CURRENT_CLASS_MENU_TITLE_CLASSNAME} ${currentClassTextClass}`}
                              title={currentClass.title}
                            >
                              {currentClass.title}
                            </p>
                            {currentClass.status === 'ENDED' ? (
                              <span className="inline-flex rounded-base border-2 border-amber-500 bg-amber-50 px-1.5 py-0.5 text-[9px] font-heading uppercase tracking-wider text-amber-700 dark:border-amber-600 dark:bg-amber-950/40 dark:text-amber-400">
                                Ended
                              </span>
                            ) : null}
                          </div>
                          {currentClass.topics.length > 0 ? (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {currentClass.topics.slice(0, 3).map((topic) => (
                                <span
                                  key={topic}
                                  className="inline-flex max-w-full items-center rounded-base border-2 border-black px-2 py-0.5 text-[10px] font-heading text-black"
                                  style={{ backgroundColor: getClassroomPalette({ title: topic }).light }}
                                >
                                  <span className="truncate">{topic}</span>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className={`mt-1 text-xs ${currentClassTextClass}`}>No topics yet</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <Link
                      to={`/classes/${currentClass.id}`}
                      className={`mt-3 flex min-h-11 w-full items-center gap-2 rounded-base border-2 px-3 py-2 text-sm font-heading transition-all duration-200 ${
                        isClassHomeActive
                          ? 'border-nb-border bg-main text-main-foreground nb-shadow'
                          : 'border-nb-border bg-secondary-bg text-foreground nb-shadow-sm hover:bg-main hover:text-main-foreground'
                      }`}
                    >
                      <Home className="h-4 w-4" />
                      <span>Class Home</span>
                    </Link>
                  </div>
                ) : null}

                <div className="mb-2">
                  <p className="px-2.5 py-1 text-[11px] font-heading text-foreground/50 uppercase tracking-wider">
                    Chats
                  </p>
                </div>
                {currentClass && classChatItems.length === 0 ? (
                  <p className="px-2.5 py-4 text-xs font-base text-foreground/40">
                    No chats for this class yet.
                  </p>
                ) : (
                  classChatItems.map((session) => (
                    <SessionListItem
                      key={session.id}
                      session={session}
                      isActive={session.id === currentSessionId}
                      onSelect={() => selectSession(session.id)}
                      onDelete={() => deleteSession(session.id)}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
